//Angular Core
import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostListener, inject } from '@angular/core';
import { Router } from '@angular/router';

// Angular Modules
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';

// Material Modules
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

// Third Party Modules
import { ToastrModule } from 'ngx-toastr';

// Services
import { ToastrService } from 'ngx-toastr';
import { TokenConfigs } from '../../interfaces/TokenConfigs';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { TokenService } from '../../services/token/token.service';
import { ValidatorService } from '../../services/validator/validator.service';

//Interfaces
import { GenericInput } from '../../components/GenericInput/generic-input.component';
import { FormControlStructure } from '../../interfaces/FormControlStructure';
import { InputValidator } from '../../interfaces/GenericInputConfigs';
import { UserCredentials } from '../../interfaces/User';

//Menu Configs
import { MenuService } from '../../services/menu/menu.service';
import { PermissionService } from '../../services/permission/permission.service';
import { formControls } from './menu-configs';

@Component({
  selector: 'Login',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    ToastrModule,
    MatProgressSpinnerModule,
    GenericInput,
  ],
  templateUrl: './login-page.component.html',
  styleUrl: './login-page.component.scss',
})
export class LoginPage {
  form!: FormGroup;
  windowSize!: number;
  loading: boolean = false;
  rememberMe: boolean = false;
  formControls: FormControlStructure[] = formControls;

  // Dependency Injections
  private router: Router = inject(Router);
  private authService: AuthenticationService = inject(AuthenticationService);
  private toastr: ToastrService = inject(ToastrService);
  private tokenService: TokenService = inject(TokenService);
  private validatorsService: ValidatorService = inject(ValidatorService);
  private permissionService: PermissionService = inject(PermissionService);
  private menuService: MenuService = inject(MenuService);

  constructor() {
    this.form = new FormGroup({});

    this.formControls.map((formControl) => {
      this.form.addControl(
        formControl['controlName'],
        new FormControl(formControl.initialValue, formControl.validators)
      );
    });

    this.form?.get('email')?.valueChanges.subscribe((email: string) => {
      if (!this.form?.get('email')?.errors) {
        this.resolveInvalidFieldError('password');
      }

      let userCredentials: Array<UserCredentials> = [];

      let localStorageUsers = localStorage.getItem('saved-users');

      if (localStorageUsers) {
        userCredentials = JSON.parse(localStorageUsers);
      }

      if (userCredentials.length > 0) {
        let user = userCredentials.find(
          (user: UserCredentials) => user.email === email
        );

        if (user) {
          this.form?.get('password')?.setValue(user.password);
        }
      }
    });

    this.form?.get('password')?.valueChanges.subscribe((password: string) => {
      if (!this.form?.get('password')?.errors) {
        this.resolveInvalidFieldError('email');
      }
    });
  }

  ngOnInit(): void {
    if (typeof window !== 'undefined') {
      this.windowSize = window.innerWidth;
    }

    sessionStorage.clear();
  }

  login(): void {
    this.loading = true;

    let { email, password } = this.form?.getRawValue();

    this.authService.authenticate({ email, password }).subscribe({
      next: (response: TokenConfigs) => {
        this.loading = false;

        response.access_token = `Bearer ${response.access_token}`;

        if (this.rememberMe) {
          this.authService.setRememberMe(email, password);
        }

        let decodedToken = this.tokenService.decodeToken(response.access_token);
        decodedToken.token = response.access_token;

        this.tokenService.setToken(decodedToken);
        this.tokenService.storeTokenInSession(response);

        this.toastr.success('Login realizado com sucesso!');

        this.permissionService.setUserPermissions();

        const routeToRedirect = this.menuService.getFirstMenuUserCanAccess();

        this.router.navigate([routeToRedirect]);
      },
      error: (errorResponse: HttpErrorResponse) => {
        this.toastr.error(errorResponse.error.error);

        this.form.get('email')?.setErrors({ invalid: true });
        this.form.get('password')?.setErrors({ invalid: true });

        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  forgotPassword(): void {
    this.router.navigate(['/recover-password']);
  }

  getValidators(controlName: string): InputValidator | undefined {
    return this.validatorsService.getValidators(controlName, this.formControls);
  }

  resolveInvalidFieldError(field: string): void {
    if (this.form.get(field)?.hasError('invalid')) {
      delete this.form.get(field)?.errors?.['invalid'];
      this.form.get(field)?.updateValueAndValidity();
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: UIEvent): void {
    let eventTarget = event?.target as Window;
    this.windowSize = eventTarget.innerWidth;
  }
}
